import { streamAuthHeaders } from 'src/services/sse';

import { DataExportError, DataExportResult, DataExportRequest, DataExportProgress } from './data-export.types';

/**
 * The data export (JT1-1913). A raw `fetch` rather than an RTK Query endpoint: the server streams
 * the CSV as it reads the records, and reading the body as it arrives is what lets the dialog show
 * how far a large export has got.
 */

/** The `filename` of a Content-Disposition header, or `fallback` when it has none. */
export const fileNameFrom = (header: string | null, fallback: string): string =>
  /filename="([^"]+)"/.exec(header ?? '')?.[1] ?? fallback;

/** Line ends in a piece of the file. A note holding line breaks counts high; the caller caps it. */
const lineEnds = (text: string): number => text.split('\r\n').length - 1;

export async function downloadDataExport(
  organizationId: string,
  request: DataExportRequest,
  onProgress?: (progress: DataExportProgress) => void
): Promise<DataExportResult> {
  const response = await fetch(`${import.meta.env.VITE_BACKEND_URL}/organizations/${organizationId}/exports`, {
    method: 'POST',
    headers: { ...streamAuthHeaders(), Accept: 'text/csv', 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new DataExportError(response.status, body?.error_code, body?.context);
  }

  const total = Number(response.headers.get('X-Export-Total') ?? 0);
  const fileName = fileNameFrom(response.headers.get('Content-Disposition'), `${request.entity}-export.csv`);

  if (!response.body) {
    return { blob: await response.blob(), fileName, total };
  }

  // The server breaks the connection if it fails part way, so a read that throws is a failed
  // export, never a short file.
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  const chunks: Uint8Array[] = [];
  let ends = 0;

  for (;;) {
    // eslint-disable-next-line no-await-in-loop
    const { done, value } = await reader.read();
    if (done) break;

    chunks.push(value);
    ends += lineEnds(decoder.decode(value, { stream: true }));
    // The first line end closes the heading row.
    onProgress?.({ written: Math.min(Math.max(ends - 1, 0), total), total });
  }

  onProgress?.({ written: total, total });

  return { blob: new Blob(chunks, { type: 'text/csv;charset=utf-8' }), fileName, total };
}

/** Hands a downloaded file to the browser to save. */
export function saveFile(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
